/**
 * Download utilities for direct file downloads via signed URLs
 */

import type { ModelBlob, ProgressCallback } from '@stw/types';

import { getCurrentToken } from '@/api/auth';

/**
 * Get authentication token for API requests
 * @returns Auth token or null if not authenticated
 */
const getAuthToken = async (): Promise<string | null> => {
  try {
    return await getCurrentToken();
  } catch (error) {
    console.error('[getAuthToken] Failed to get auth token:', error);
    return null;
  }
};

/**
 * Stream response body to ArrayBuffer with progress tracking
 * DRY: Common streaming logic extracted to avoid duplication
 */
const streamToArrayBuffer = async (
  response: Response,
  onProgress?: ProgressCallback
): Promise<ArrayBuffer> => {
  const contentLength = response.headers.get('Content-Length');
  const fileSize = contentLength ? parseInt(contentLength, 10) : 0;

  if (!response.body) {
    throw new Error('Response body is null');
  }

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let receivedLength = 0;

  try {
    let done = false;
    while (!done) {
      const result = await reader.read();
      done = result.done;

      if (!done && result.value) {
        chunks.push(result.value);
        receivedLength += result.value.length;

        if (onProgress && fileSize > 0) {
          onProgress(receivedLength, fileSize);
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  const result = new Uint8Array(receivedLength);
  let position = 0;
  for (const chunk of chunks) {
    result.set(chunk, position);
    position += chunk.length;
  }

  return result.buffer;
};

/**
 * Download and concatenate chunks with progress tracking
 * @param chunks - Array of chunk objects with index and URL
 * @param chunkCount - Total number of chunks
 * @param onProgress - Optional callback for download progress updates
 * @returns Promise resolving to concatenated ArrayBuffer
 */
const downloadAndConcatenateChunks = async (
  chunks: Array<{ index: number; url: string }>,
  chunkCount: number,
  onProgress?: ProgressCallback
): Promise<ArrayBuffer> => {
  const CONCURRENT_DOWNLOADS = 4;
  const chunkBuffers: ArrayBuffer[] = new Array(chunkCount);
  const chunkSizes: number[] = new Array(chunkCount).fill(0);
  const chunkProgress: number[] = new Array(chunkCount).fill(0);
  let totalBytesExpected = 0;

  // Download chunks with concurrency limit
  const downloadChunk = async (chunk: {
    index: number;
    url: string;
  }): Promise<void> => {
    const response = await fetch(chunk.url);

    if (!response.ok) {
      throw new Error(
        `Failed to download chunk ${chunk.index}: ${response.status}`
      );
    }

    const contentLength = response.headers.get('Content-Length');
    const chunkSize = contentLength ? parseInt(contentLength, 10) : 0;

    if (chunkSize > 0) {
      chunkSizes[chunk.index] = chunkSize;
      // Calculate total expected bytes from all chunks
      totalBytesExpected = chunkSizes.reduce((sum, size) => sum + size, 0);
    }

    const buffer = await streamToArrayBuffer(response, (loaded, total) => {
      // Update progress for this specific chunk
      chunkProgress[chunk.index] = loaded;

      // Calculate total progress across all chunks
      const totalBytesDownloaded = chunkProgress.reduce(
        (sum, progress) => sum + progress,
        0
      );

      if (onProgress && totalBytesExpected > 0) {
        onProgress(totalBytesDownloaded, totalBytesExpected);
      }
    });

    chunkBuffers[chunk.index] = buffer;
  };

  // Download chunks in batches with concurrency limit
  for (let i = 0; i < chunks.length; i += CONCURRENT_DOWNLOADS) {
    const batch = chunks.slice(i, i + CONCURRENT_DOWNLOADS);
    await Promise.all(batch.map(downloadChunk));
  }

  // Concatenate all chunks in order
  const totalLength = chunkBuffers.reduce(
    (sum, buffer) => sum + buffer.byteLength,
    0
  );
  const result = new Uint8Array(totalLength);
  let offset = 0;

  for (const buffer of chunkBuffers) {
    result.set(new Uint8Array(buffer), offset);
    offset += buffer.byteLength;
  }

  return result.buffer;
};

/**
 * Download file directly from GCS using signed URL or chunks
 * @param itemId - Item ID to download
 * @param onProgress - Optional callback for download progress updates
 * @returns Promise resolving to complete file as ArrayBuffer
 * @throws Error if download fails at any stage
 */
export const downloadFile = async (
  itemId: string,
  onProgress?: ProgressCallback
): Promise<ArrayBuffer> => {
  const API_URL = import.meta.env.VITE_API_URL;

  if (!API_URL) {
    throw new Error('VITE_API_URL environment variable is not set');
  }

  const token = await getAuthToken();

  // Try to get chunks first
  const chunksUrl = `${API_URL}/item/${itemId}/chunks`;
  const chunksResponse = await fetch(chunksUrl, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (chunksResponse.ok) {
    // Model is chunked - download and concatenate chunks
    const { data } = await chunksResponse.json();
    return downloadAndConcatenateChunks(
      data.chunks,
      data.chunk_count,
      onProgress
    );
  }

  // Fallback to original single-file download (for backward compatibility)
  const urlEndpoint = `${API_URL}/item/${itemId}/file/url`;
  const urlResponse = await fetch(urlEndpoint, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!urlResponse.ok) {
    const errorText = await urlResponse.text();
    throw new Error(
      `Failed to get download URL: ${urlResponse.status} - ${errorText}`
    );
  }

  const { data } = await urlResponse.json();
  const signedUrl = data.url;

  if (!signedUrl) {
    throw new Error('No signed URL in response');
  }

  // Download directly from GCS using signed URL
  const response = await fetch(signedUrl);

  if (!response.ok) {
    throw new Error(`Failed to download file: ${response.status}`);
  }

  return streamToArrayBuffer(response, onProgress);
};

/**
 * Create model blob for 3D viewers with cleanup function
 * @param arrayBuffer - File data as ArrayBuffer
 * @returns Object with blob URL and cleanup function
 * @throws Error if ArrayBuffer is empty or corrupted
 * @example
 * ```typescript
 * const { blobUrl, cleanup } = createModelBlob(arrayBuffer);
 * // Use blobUrl with 3D viewer
 * // Later: cleanup(); // Clean up blob URL
 * ```
 */
export const createModelBlob = (arrayBuffer: ArrayBuffer): ModelBlob => {
  if (!arrayBuffer || arrayBuffer.byteLength === 0) {
    throw new Error('Downloaded file is empty or corrupted');
  }

  const blob = new Blob([arrayBuffer], { type: 'application/octet-stream' });
  const blobUrl = URL.createObjectURL(blob);

  const cleanup = () => {
    try {
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      // Ignore cleanup errors silently
    }
  };

  return { blobUrl, cleanup };
};

/**
 * Trigger browser download from blob data
 * @param blob - Blob data to download
 * @param filename - Suggested filename for download
 * @example
 * ```typescript
 * const blob = new Blob([data], { type: 'model/gltf-binary' });
 * triggerBrowserDownload(blob, 'model.glb');
 * ```
 */
export const triggerBrowserDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Download from external URL and trigger browser download
 * @param url - External URL to download from
 * @param filename - Suggested filename for download
 * @throws Error if download fails
 * @example
 * ```typescript
 * await downloadFromUrl('https://example.com/model.glb', 'model.glb');
 * ```
 */
export const downloadFromUrl = async (
  url: string,
  filename: string
): Promise<void> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download from URL: ${response.status}`);
  }

  const blob = await response.blob();
  triggerBrowserDownload(blob, filename);
};
